import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  // Setup form
  const [setupForm, setSetupForm] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    full_name: "",
    email: "",
    phone: "",
  });
  const [showSetupPassword, setShowSetupPassword] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
      return;
    }
    checkSetup();
  }, [isAuthenticated, navigate]);

  const checkSetup = async () => {
    try {
      const res = await axios.get(`${API}/auth/check-setup`);
      setNeedsSetup(!res.data.admin_exists);
    } catch {
      setNeedsSetup(false);
    } finally {
      setChecking(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please enter username and password");
      return;
    }
    setLoading(true);
    const result = await login(username, password);
    setLoading(false);
    if (result.success) {
      const role = result.user?.role;
      if (role === "warehouse_manager") navigate("/invoices");
      else if (role === "executive") navigate("/customers");
      else navigate("/dashboard");
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    const { username, password, confirmPassword, full_name, email, phone } = setupForm;

    if (!username || !password || !full_name) {
      toast.error("Please fill all required fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/setup`, {
        username,
        password,
        full_name,
        email: email || null,
        phone: phone || null,
      });
      const { access_token, user } = res.data;
      localStorage.setItem("token", access_token);
      toast.success(`Welcome, ${user.full_name}! Admin account created.`);
      window.location.href = "/dashboard";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="login-container">
        <div className="login-card animate-fade-in text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // First-time Admin Setup
  if (needsSetup) {
    return (
      <div className="login-container">
        <div className="login-card animate-fade-in" style={{ maxWidth: 440 }}>
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-2xl font-black tracking-tight mb-1">Setup Admin Account</h1>
            <p className="text-muted-foreground text-sm">
              Create your administrator account to get started
            </p>
          </div>

          <form onSubmit={handleSetup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="setup-name">Full Name *</Label>
              <Input
                id="setup-name"
                placeholder="Your full name"
                value={setupForm.full_name}
                onChange={(e) => setSetupForm({ ...setupForm, full_name: e.target.value })}
                className="h-11"
                data-testid="setup-fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup-username">Username *</Label>
              <Input
                id="setup-username"
                placeholder="Choose a username"
                value={setupForm.username}
                onChange={(e) => setSetupForm({ ...setupForm, username: e.target.value })}
                className="h-11"
                data-testid="setup-username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup-password">Password *</Label>
              <div className="relative">
                <Input
                  id="setup-password"
                  type={showSetupPassword ? "text" : "password"}
                  placeholder="Min 6 characters"
                  value={setupForm.password}
                  onChange={(e) => setSetupForm({ ...setupForm, password: e.target.value })}
                  className="h-11 pr-10"
                  data-testid="setup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSetupPassword(!showSetupPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showSetupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="setup-confirm">Confirm Password *</Label>
              <Input
                id="setup-confirm"
                type="password"
                placeholder="Re-enter password"
                value={setupForm.confirmPassword}
                onChange={(e) => setSetupForm({ ...setupForm, confirmPassword: e.target.value })}
                className="h-11"
                data-testid="setup-confirm-password-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="setup-email">Email</Label>
                <Input
                  id="setup-email"
                  type="email"
                  placeholder="admin@company.com"
                  value={setupForm.email}
                  onChange={(e) => setSetupForm({ ...setupForm, email: e.target.value })}
                  data-testid="setup-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="setup-phone">Phone</Label>
                <Input
                  id="setup-phone"
                  placeholder="Phone number"
                  value={setupForm.phone}
                  onChange={(e) => setSetupForm({ ...setupForm, phone: e.target.value })}
                  data-testid="setup-phone-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 btn-cta"
              disabled={loading}
              data-testid="setup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Create Admin Account"
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Normal Login
  return (
    <div className="login-container">
      <div className="login-card animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black tracking-tight mb-2">Retail CRM</h1>
          <p className="text-muted-foreground text-sm">Electronics & Home Appliances</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-11"
              data-testid="username-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 pr-10"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="toggle-password-btn"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 btn-cta"
            disabled={loading}
            data-testid="login-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
